/**
 * Search Niche API Service
 * Secure and optimized API calls for niche search operations
 */

import { apiClient } from '@/auth';
import { SEARCH_NICHE_CONFIG } from '@Constants/searchNiche.config';
import type { ApiResponse } from '@http';
import type { AppDispatch } from '../store';
import type { SearchNicheFilters, SearchNicheResult, AnalysisTags, Product } from '../slices/searchNicheSlice';
import { subscriptionApi } from '../slices/subscriptionSlice';

// Constants
export const STORAGE_KEY_ANALYSIS_ID = SEARCH_NICHE_CONFIG.ANALYSIS_ID_STORAGE_KEY;
export const STORAGE_KEY_STAGE = SEARCH_NICHE_CONFIG.SEARCH_STAGE_STORAGE_KEY;
export const POLLING_INTERVAL = SEARCH_NICHE_CONFIG.pollInterval;
export const MAX_STAGE = SEARCH_NICHE_CONFIG.MAX_STAGE;

// Analysis request parameters interface
export interface NicheAnalysisParams extends Record<string, unknown> {
  keyword: string;
  price_min?: number;
  price_max?: number;
  revenue_30d_min?: number;
  revenue_30d_max?: number;
  reviews_count_min?: number;
  reviews_count_max?: number;
  num_keywords_to_analyse?: number;
  num_niche_to_find?: number;
  num_products_per_niche?: number;
  num_products_to_analyse?: number;
  asins?: string[];
}

// Analysis response interfaces
export interface InitiateAnalysisResponse {
  analysis_id: number;
}

// Raw API response from state endpoint
export interface RawAnalysisStateResponse {
  analysis_id: number;
  state: {
    stage: number;
    update_datetime: string;
    niches: SearchNicheResult[] | null;
    tags: AnalysisTags | null;
  };
}

// Processed analysis state for frontend use
export interface AnalysisStateResponse {
  status: 'pending' | 'processing' | 'completed';
  progress: number;
  niches?: SearchNicheResult[];
  analysisTags?: AnalysisTags;
  stage: number;
  error?: string;
}

// Demo niche interface
export interface DemoNiche {
  id: number;
  keywords: string;
  data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Demo niche detail interface
export interface DemoNicheDetail {
  id: number;
  keywords: string;
  data: {
    meta: Record<string, string>;
    tags: AnalysisTags;
    niches: SearchNicheResult[];
  };
  created_at: string;
  updated_at: string;
}

// Product analysis interfaces
export interface ProductAnalysis {
  opportunity: string;
  strengths: string;
  verdict: string;
  weaknesses: string;
}

export interface ProductAnalysisResponse {
  analysis: ProductAnalysis;
  analysis_id: number;
  asin: string;
}

// ASIN product lookup response
export interface AsinProductResponse {
  product_name: string;
  asin: string;
  product_url: string;
  product_desc: string;
  price: number;
  price_currency: string | null;
  rating: number;
  reviews_summary: string | null;
  product_score: number;
  images_urls: string[];
  metrics: Record<string, unknown>;
  tags: Record<string, string>;
  bsr_category_data: { category: string | null; rank: number }[];
}

// Keywords by title response
export interface KeywordByTitleItem {
  keyword: string;
  relevance: number;
  keyword_type: string;
}

export interface KeywordsByTitleResponse {
  success: boolean;
  data: {
    product_title: string;
    inferred_product: string;
    dominant_intent: string;
    keyword_count: number;
    keywords: KeywordByTitleItem[];
  };
  error: string;
}

// Trends analysis interfaces
export interface TrendAmazonKeyword {
  keyword: string;
  product_type: string;
  why_relevant: string;
  specificity: string;
}

export interface TrendResult {
  trend_query: string;
  canonical_trend: string;
  keep: boolean;
  reason: string;
  trend_type: string;
  productability_score: number;
  safe_product_angle: string;
  amazon_keywords: TrendAmazonKeyword[];
  excluded_angles: string[];
}

export interface TrendsAnalysisResponse {
  geo: string;
  total: number;
  results: TrendResult[];
}

// Summary score item
export interface NicheSummaryScoreItem {
  summary: string;
  score: number;
}

// Per-keyword summary data
export interface NicheSummaryData {
  keyword: string;
  demand_trend_dynamics: NicheSummaryScoreItem;
  market_depth_financial_potential: NicheSummaryScoreItem;
  price_segmentation_revenue_distribution: NicheSummaryScoreItem;
  competition_structure_monopoly: NicheSummaryScoreItem;
  barriers_to_entry_social_proof: NicheSummaryScoreItem;
  niche_homogeneity_strategy: NicheSummaryScoreItem;
  advertising_traffic_cost: NicheSummaryScoreItem;
  overall_niche_analysis_summary: string;
  overall_niche_score: number;
}

// Individual summary session
export interface NicheSummarySession {
  session_id: string;
  keyword: string;
  status: string;
  summary: NicheSummaryData;
  error: string | null;
}

// Full response from /reports/{analysis_id}/summary
export interface AnalysisSummaryResponse {
  analysis_id: number;
  summaries: NicheSummarySession[];
}

// Search history item
export interface SearchHistoryItem {
  analysis_id: number;
  search_params: {
    keyword: string;
    marketplace: string;
    exact_keyword: boolean;
    category: string | null;
    price_min: number | null;
    price_max: number | null;
    rating_min: number | null;
    rating_max: number | null;
    revenue_30d_min: number | null;
    revenue_30d_max: number | null;
    reviews_count_min: number | null;
    reviews_count_max: number | null;
    num_keywords_to_analyse: number;
    num_niche_to_find: number;
    num_products_per_niche: number;
    num_products_to_analyse: number;
  };
  created_at: string;
}

// Helper function to transform filters to API parameters
const transformFiltersToParams = (filters: SearchNicheFilters): NicheAnalysisParams => ({
  keyword: filters.keyword,
  exact_keyword: filters.exactKeyword,
  marketplace: filters.marketplace,
  // Hardcoded parameters as requested
  num_keywords_to_analyse: 50,
  num_niche_to_find: filters.exactKeyword ? 1 : 3,
  num_products_per_niche: 200,
  num_products_to_analyse: 300, //back, not work from front
  ...(filters.asins?.length ? { asins: filters.asins } : {}),
});

// Search Niche API service class
export class SearchNicheApiService {
  private static pollingInterval: NodeJS.Timeout | null = null;
  private static isPolling = false;
  private static hasFetchedSummary = false;
  private static hasCompleted = false;

  /**
   * Start niche analysis with provided filters
   */
  static async initiateNicheAnalysis(filters: SearchNicheFilters): Promise<InitiateAnalysisResponse> {
    try {
      const analysisParams = transformFiltersToParams(filters);

      const response: ApiResponse<InitiateAnalysisResponse> = await apiClient.post<InitiateAnalysisResponse>(
        'sellers-agents/analysis',
        analysisParams as Record<string, unknown>
      );

      // Store analysis ID in localStorage for persistence
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY_ANALYSIS_ID, String(response.data.analysis_id));
        localStorage.setItem(STORAGE_KEY_STAGE, '0');
      }

      // Сбросить флаги для нового анализа
      this.hasFetchedSummary = false;

      return response.data;
    } catch (error) {
      console.error('Failed to initiate niche analysis:', error);
      throw error;
    }
  }

  /**
   * Get the current state of a niche analysis
   */
  static async getAnalysisState(analysisId: number): Promise<AnalysisStateResponse> {
    try {
      const response: ApiResponse<RawAnalysisStateResponse> = await apiClient.get<RawAnalysisStateResponse>(
        `sellers-agents/analysis/${analysisId}/state`
      );

      const { state } = response.data;

      // Calculate progress as percentage (stage / MAX_STAGE)
      let progress = Math.round((state.stage / MAX_STAGE) * 100);
      if(state.stage < 1){
        progress = 10;
      }

      // Determine status based on stage
      const status = state.stage >= MAX_STAGE
        ? 'completed'
        : state.stage > 0
          ? 'processing'
          : 'pending';

      // Store current stage in localStorage for persistence
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY_STAGE, String(state.stage));
      }

      // Build response object
      const result: AnalysisStateResponse = {
        status,
        progress,
        stage: state.stage,
      };

      // Add niches if available (stage >= 2)
      if (state.niches && state.niches.length > 0) {
        result.niches = state.niches;
      }

      // Add analysis tags if available (stage 5)
      if (state.tags) {
        result.analysisTags = state.tags;
        if (state.niches) {
          result.niches = state.niches;
        }
      }

      return result;
    } catch (error) {
      console.error(`Failed to get analysis state for ID ${analysisId}:`, error);
      throw error;
    }
  }

  /**
   * Get the summary report for a completed analysis
   */
  static async getSummary(analysisId: number): Promise<AnalysisSummaryResponse> {
    const response: ApiResponse<AnalysisSummaryResponse> = await apiClient.get<AnalysisSummaryResponse>(
      `reports/${analysisId}/summary`
    );
    return response.data;
  }

  /**
   * Start polling for analysis state updates
   */
  static startPolling(
    analysisId: number,
    dispatch: AppDispatch,
    onUpdate?: (state: AnalysisStateResponse) => void,
    onComplete?: (niches: SearchNicheResult[], analysisTags?: AnalysisTags) => void,
    onSummary?: (summary: AnalysisSummaryResponse) => void
  ): void {
    if (this.isPolling) {
      return;
    }

    this.isPolling = true;
    this.hasFetchedSummary = false;
    this.hasCompleted = false;
    console.log('Starting polling for analysis ID:', analysisId);

    const pollFunction = async () => {
      try {
        if (!analysisId) {
          return;
        }

        const state = await this.getAnalysisState(analysisId);

        if (onUpdate) {
          onUpdate(state);
        }

        if (state.stage >= 2 && !this.hasFetchedSummary) {
          console.log('Fetching summary for analysis ID:', analysisId);
          this.hasFetchedSummary = true;
          this.getSummary(analysisId)
            .then(summary => {
              if (onSummary) {
                onSummary(summary);
              }
            })
            .catch(error => {
              console.error('Failed to fetch summary:', error);
            });
        }

        if ((state.status === 'completed' || state.stage >= MAX_STAGE) && !this.hasCompleted) {
          // Guards against a concurrent poll tick (e.g. a slow request overlapping
          // the next interval) re-running completion and firing onComplete/invalidate twice.
          this.hasCompleted = true;

          if (onComplete && state.niches) {
            onComplete(state.niches, state.analysisTags);
          }
          // Refresh the cached subscription usage so the sidebar balance is
          // current on every flow that lands here (fresh search, resume,
          // load-last, history). The `charge_tokens` analytics emit does NOT
          // live here — this branch also runs when reopening an
          // already-completed analysis, which would emit a phantom charge per
          // page load; the fresh-start onComplete callback emits it instead,
          // same gating as research_completed.
          dispatch(subscriptionApi.util.invalidateTags(['SubscriptionUsage']));
          this.stopPolling();
        }
      } catch (error) {
        console.error('Error during polling:', error);
      }
    };

    pollFunction();
    this.pollingInterval = setInterval(pollFunction, POLLING_INTERVAL);
  }

  /**
   * Stop polling for analysis status
   */
  static stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
    console.log('Polling stopped');
  }

  /**
   * Check if polling is currently active
   */
  static isPollingActive(): boolean {
    return this.isPolling;
  }

  /**
   * Resume polling if there's a saved analysis ID and it's not completed
   */
  static resumePollingIfNeeded(
    dispatch: AppDispatch,
    onUpdate?: (state: AnalysisStateResponse) => void,
    onComplete?: (niches: SearchNicheResult[], analysisTags?: AnalysisTags) => void,
    onSummary?: (summary: AnalysisSummaryResponse) => void
  ): void {
    if (typeof window === 'undefined') {
      return;
    }

    const savedAnalysisId = localStorage.getItem(STORAGE_KEY_ANALYSIS_ID);

    if (savedAnalysisId) {
      const analysisId = Number(savedAnalysisId);
      this.startPolling(analysisId, dispatch, onUpdate, onComplete, onSummary);
    }
  }

  /**
   * Reset all polling and stored data
   */
  static reset(): void {
    this.stopPolling();
    this.hasFetchedSummary = false;
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY_ANALYSIS_ID);
      localStorage.removeItem(STORAGE_KEY_STAGE);
    }
  }

  /**
   * Get demo niches suggestions
   */
  static async getDemoNiches(): Promise<DemoNiche[]> {
    try {
      const response: ApiResponse<DemoNiche[]> = await apiClient.get<DemoNiche[]>(
        'demo-niches/'
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get demo niches:', error);
      throw error;
    }
  }

  /**
   * Get demo niche data by ID
   */
  static async getDemoNicheById(id: number): Promise<DemoNicheDetail> {
    try {
      const response: ApiResponse<DemoNicheDetail> = await apiClient.get<DemoNicheDetail>(
        `demo-niches/${id}`
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get demo niche:', error);
      throw error;
    }
  }

  /**
   * Get search history
   */
  static async getSearchHistory(limit: number = 1, offset: number = 0): Promise<SearchHistoryItem[]> {
    try {
      const response: ApiResponse<SearchHistoryItem[]> = await apiClient.get<SearchHistoryItem[]>(
        'sellers-agents/searches',
        { limit, offset }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get search history:', error);
      throw error;
    }
  }

  /**
   * Look up product info by ASIN
   */
  static async getProductByAsin(asin: string, marketplace: string): Promise<AsinProductResponse> {
    const response: ApiResponse<AsinProductResponse> = await apiClient.get<AsinProductResponse>(
      `sellers-agents/products/${encodeURIComponent(asin)}`,
      { marketplace }
    );
    return response.data;
  }

  /**
   * Get keywords by product title
   */
  static async getKeywordsByTitle(title: string): Promise<KeywordsByTitleResponse> {
    const response: ApiResponse<KeywordsByTitleResponse> = await apiClient.post<KeywordsByTitleResponse>(
      'keywords/by-title',
      { title }
    );
    return response.data;
  }
}

/**
 * Analyze a specific product
 */
export async function analyzeProduct(
  analysisId: number,
  asin: string
): Promise<ProductAnalysisResponse> {
  try {
    const response: ApiResponse<ProductAnalysisResponse> = await apiClient.post<ProductAnalysisResponse>(
      `sellers-agents/analysis/${analysisId}/products/${asin}/analyze`
    );

    return response.data;
  } catch (error) {
    console.error('Failed to analyze product:', error);
    throw error;
  }
}
